import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import styles from './CreateReviewModal.module.css';
import { Modal } from '../../../components/UI/Modal/Modal';
import { Button } from '../../../components/UI/Button/Button';
import { CreateReviewRequest } from '../../../api/dto/reviews';
import { Review } from '../../../models/reviews';
import { MediaAsset } from '../../../models/media';
import { CourseSelector } from './CourseSelector';
import { PersonSelector } from './PersonSelector';
import CountrySelector from './CountrySelector';
import CitySelector from './CitySelector';
import TagSelector from './TagSelector/TagSelector';
import { MediaSelector } from '../../../components/UI/MediaSelector';
import { VideoSelector } from '../../../components/UI/VideoSelector';

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    reviewData: CreateReviewRequest,
    reviewId?: string,
  ) => Promise<boolean>;
  loading?: boolean;
  reviewId?: string; // Если передан ID, то это редактирование существующего отзыва
  existingReview?: Review | null; // Данные существующего отзыва для редактирования
}

export const ReviewModal: React.FC<ReviewModalProps> = observer(
  ({ isOpen, onClose, onSubmit, reviewId, existingReview }) => {
    const [loading, setLoading] = useState(false);
    const [selectedHeroMedia, setSelectedHeroMedia] =
      useState<MediaAsset | null>(null);
    const [selectedVideoMedia, setSelectedVideoMedia] =
      useState<MediaAsset | null>(null);
    const [formData, setFormData] = useState<CreateReviewRequest>({
      hero_media_id: '',
      video_provider: undefined,
      video_id: undefined,
      video_url: undefined,
      video_duration_sec: undefined,
      status: 'draft',
      rating: 5,
      is_featured: false,
      translations: [
        {
          locale: 'ru',
          slug: '',
          title: '',
          summary: '',
          body: '',
          transcript_text: '',
          seo_title: '',
          seo_description: '',
          alt_hero: '',
        },
        {
          locale: 'en',
          slug: '',
          title: '',
          summary: '',
          body: '',
          transcript_text: '',
          seo_title: '',
          seo_description: '',
          alt_hero: '',
        },
      ],
      course_id: '',
      author_id: '',
      country_id: '',
      city_id: '',
      tag_ids: [],
    });

    // Функция для извлечения YouTube video ID из различных форматов URL
    const extractYouTubeVideoId = (url: string): string | null => {
      if (!url) {
        return null;
      }

      // Различные паттерны YouTube URL
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
        /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
        /youtu\.be\/([^&\n?#]+)/,
        /youtube\.com\/embed\/([^&\n?#]+)/,
        /youtube\.com\/v\/([^&\n?#]+)/,
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
          return match[1];
        }
      }

      // Если не удалось извлечь из URL, возможно это уже video_id
      if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
        return url;
      }

      return null;
    };

    // Функция для извлечения Accelsite video ID из URL
    const extractAccelsiteVideoId = (url: string): string | null => {
      if (!url) {
        return null;
      }

      // Паттерн для Accelsite URL: https://v.accelsite.io/v/VIDEO_ID
      const accelsitePattern = /(?:v\.accelsite\.io\/v\/)([^&\n?#]+)/;
      const match = url.match(accelsitePattern);

      if (match && match[1]) {
        return match[1];
      }

      return null;
    };

    // Функция для генерации YouTube thumbnail с fallback вариантами
    const generateYouTubeThumbnail = (videoId: string): string => {
      // Пробуем разные качества thumbnail:
      // maxresdefault - самое высокое качество (1280x720)
      // hqdefault - высокое качество (480x360)
      // mqdefault - среднее качество (320x180)
      // default - стандартное качество (120x90)
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    };

    // Функция для конвертации hero_media из Review в MediaAsset
    const convertHeroMediaToAsset = (
      heroMedia: Review['hero_media'],
    ): MediaAsset | null => {
      if (!heroMedia) {
        return null;
      }

      return {
        id: heroMedia.id,
        type: heroMedia.type as 'image' | 'video',
        provider: heroMedia.provider as 's3' | 'youtube',
        original_url: heroMedia.original_url,
        variants: heroMedia.variants as unknown as MediaAsset['variants'], // Приводим к типу MediaAsset
        width: heroMedia.width,
        height: heroMedia.height,
        size_bytes: heroMedia.size_bytes,
        mime: heroMedia.mime,
        created_at: heroMedia.created_at,
        updated_at: heroMedia.updated_at,
      };
    };

    // Функция для создания MediaAsset из данных видео
    const convertVideoDataToAsset = (review: Review): MediaAsset | null => {
      if (!review.video_id || !review.video_provider) {
        return null;
      }

      // Определяем провайдера и извлекаем video_id
      const provider = review.video_provider as 'youtube' | 'accelsite';
      let extractedVideoId: string | null = null;
      let embedUrl: string = review.video_url || '';
      let thumbnailUrl: string | undefined;

      if (provider === 'youtube') {
        // Для YouTube извлекаем video_id и генерируем embed URL и thumbnail
        extractedVideoId =
          extractYouTubeVideoId(review.video_url || '') ||
          extractYouTubeVideoId(review.video_id) ||
          review.video_id;

        if (extractedVideoId) {
          embedUrl =
            review.video_url ||
            `https://www.youtube.com/embed/${extractedVideoId}`;
          thumbnailUrl = generateYouTubeThumbnail(extractedVideoId);
        }
      } else if (provider === 'accelsite') {
        // Для Accelsite извлекаем video_id и используем URL напрямую
        extractedVideoId =
          extractAccelsiteVideoId(review.video_url || '') ||
          extractAccelsiteVideoId(review.video_id) ||
          review.video_id;

        if (extractedVideoId) {
          embedUrl =
            review.video_url || `https://v.accelsite.io/v/${extractedVideoId}`;
          // thumbnailUrl остается undefined для Accelsite
        }
      }

      if (!extractedVideoId) {
        return null;
      }

      // Создаем variants объект
      const variants: Partial<MediaAsset['variants']> = {
        meta: {
          video_id: extractedVideoId,
          provider: provider,
        },
        embed: {
          url: embedUrl,
        },
      };

      // Добавляем thumbnail только если он есть (для YouTube)
      if (thumbnailUrl) {
        variants.thumbnail = {
          url: thumbnailUrl,
        };
      }

      return {
        id: review.video_id, // Оставляем оригинальный ID для связи
        type: 'video',
        provider: provider,
        original_url: review.video_url || '',
        variants: variants as MediaAsset['variants'],
        created_at: review.created_at,
        updated_at: review.updated_at,
      };
    };

    // Загружаем данные существующего отзыва при редактировании
    useEffect(() => {
      if (existingReview && isOpen) {
        // Устанавливаем медиа объекты для превью
        const heroMediaAsset = convertHeroMediaToAsset(
          existingReview.hero_media,
        );
        const videoMediaAsset = convertVideoDataToAsset(existingReview);

        setSelectedHeroMedia(heroMediaAsset);
        setSelectedVideoMedia(videoMediaAsset);

        setFormData({
          hero_media_id: existingReview.hero_media?.id || '',
          video_provider: existingReview.video_provider || undefined,
          video_id: existingReview.video_id || undefined,
          video_url: existingReview.video_url || undefined,
          video_duration_sec: existingReview.video_duration_sec || undefined,
          status: existingReview.status,
          rating: existingReview.rating || 5,
          is_featured: existingReview.is_featured,
          translations: [
            {
              locale: 'ru',
              slug:
                existingReview.translations.find((t) => t.locale === 'ru')
                  ?.slug || '',
              title:
                existingReview.translations.find((t) => t.locale === 'ru')
                  ?.title || '',
              summary:
                existingReview.translations.find((t) => t.locale === 'ru')
                  ?.summary || '',
              body:
                existingReview.translations.find((t) => t.locale === 'ru')
                  ?.body || '',
              transcript_text:
                existingReview.translations.find((t) => t.locale === 'ru')
                  ?.transcript_text || '',
              seo_title:
                existingReview.translations.find((t) => t.locale === 'ru')
                  ?.seo_title || '',
              seo_description:
                existingReview.translations.find((t) => t.locale === 'ru')
                  ?.seo_description || '',
              alt_hero:
                existingReview.translations.find((t) => t.locale === 'ru')
                  ?.alt_hero || '',
            },
            {
              locale: 'en',
              slug:
                existingReview.translations.find((t) => t.locale === 'en')
                  ?.slug || '',
              title:
                existingReview.translations.find((t) => t.locale === 'en')
                  ?.title || '',
              summary:
                existingReview.translations.find((t) => t.locale === 'en')
                  ?.summary || '',
              body:
                existingReview.translations.find((t) => t.locale === 'en')
                  ?.body || '',
              transcript_text:
                existingReview.translations.find((t) => t.locale === 'en')
                  ?.transcript_text || '',
              seo_title:
                existingReview.translations.find((t) => t.locale === 'en')
                  ?.seo_title || '',
              seo_description:
                existingReview.translations.find((t) => t.locale === 'en')
                  ?.seo_description || '',
              alt_hero:
                existingReview.translations.find((t) => t.locale === 'en')
                  ?.alt_hero || '',
            },
          ],
          course_id: existingReview.course?.id || '',
          author_id: existingReview.author?.id || '',
          country_id: existingReview.country?.id || '',
          city_id: existingReview.city?.id || '',
          tag_ids: existingReview.tags?.map((tag) => tag.id) || [],
        });
      } else if (!existingReview && isOpen) {
        // Сбрасываем форму для создания нового отзыва
        setSelectedHeroMedia(null);
        setSelectedVideoMedia(null);

        setFormData({
          hero_media_id: '',
          video_provider: undefined,
          video_id: undefined,
          video_url: undefined,
          video_duration_sec: undefined,
          status: 'draft',
          rating: 5,
          is_featured: false,
          translations: [
            {
              locale: 'ru',
              slug: '',
              title: '',
              summary: '',
              body: '',
              transcript_text: '',
              seo_title: '',
              seo_description: '',
              alt_hero: '',
            },
            {
              locale: 'en',
              slug: '',
              title: '',
              summary: '',
              body: '',
              transcript_text: '',
              seo_title: '',
              seo_description: '',
              alt_hero: '',
            },
          ],
          course_id: '',
          author_id: '',
          country_id: '',
          city_id: '',
          tag_ids: [],
        });
      }
    }, [existingReview, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);

      try {
        // Подготавливаем данные для отправки
        const preparedData: CreateReviewRequest = {
          ...formData,
          // Убираем пустые строки для необязательных полей
          hero_media_id: formData.hero_media_id || undefined,
          video_provider: formData.video_provider || undefined,
          video_id: formData.video_id || undefined,
          video_url: formData.video_url || undefined,
          video_duration_sec: formData.video_duration_sec || undefined,
          published_at: formData.published_at || undefined,
          course_id: formData.course_id || undefined,
          author_id: formData.author_id || undefined,
          country_id: formData.country_id || undefined,
          city_id: formData.city_id || undefined,
          tag_ids: formData.tag_ids?.length ? formData.tag_ids : undefined,
          translations: formData.translations.map((t) => ({
            locale: t.locale,
            slug: t.slug || t.title.toLowerCase().replace(/\s+/g, '-'),
            title: t.title,
            summary: t.summary || undefined,
            body: t.body || '',
            transcript_text: t.transcript_text || undefined,
            seo_title: t.seo_title || undefined,
            seo_description: t.seo_description || undefined,
            alt_hero: t.alt_hero || undefined,
          })),
        };

        const success = await onSubmit(preparedData, reviewId);
        if (success) {
          onClose();
          // Сбрасываем форму только при создании нового отзыва
          if (!reviewId) {
            setFormData({
              hero_media_id: '',
              video_provider: undefined,
              video_id: undefined,
              video_url: undefined,
              video_duration_sec: undefined,
              status: 'draft',
              rating: 5,
              is_featured: false,
              translations: [
                {
                  locale: 'ru',
                  slug: '',
                  title: '',
                  summary: '',
                  body: '',
                  transcript_text: '',
                  seo_title: '',
                  seo_description: '',
                  alt_hero: '',
                },
                {
                  locale: 'en',
                  slug: '',
                  title: '',
                  summary: '',
                  body: '',
                  transcript_text: '',
                  seo_title: '',
                  seo_description: '',
                  alt_hero: '',
                },
              ],
              course_id: '',
              author_id: '',
              country_id: '',
              city_id: '',
              tag_ids: [],
            });
          }
        }
      } catch (error) {
        // Ошибка обработается в родительском компоненте
      } finally {
        setLoading(false);
      }
    };

    const handleInputChange = (
      field: keyof CreateReviewRequest,
      value: string | number | boolean | string[],
    ) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    const handleMediaChange = (mediaId: string | null) => {
      handleInputChange('hero_media_id', mediaId || '');
      if (!mediaId) {
        setSelectedHeroMedia(null);
      }
      // Если mediaId есть, но selectedHeroMedia нет, то медиа объект будет загружен через MediaSelector
    };

    const handleVideoChangeWithState = (
      videoData: {
        provider?: string;
        video_id?: string;
        video_url?: string;
        duration_sec?: number;
      } | null,
    ) => {
      setFormData((prev) => ({
        ...prev,
        video_provider: videoData?.provider,
        video_id: videoData?.video_id,
        video_url: videoData?.video_url,
        video_duration_sec: videoData?.duration_sec,
      }));

      if (!videoData) {
        setSelectedVideoMedia(null);
      }
      // Если videoData есть, то selectedVideoMedia обновится через VideoSelector
    };

    const handleTranslationChange = (
      index: number,
      field: string,
      value: string,
    ) => {
      setFormData((prev) => ({
        ...prev,
        translations: prev.translations.map((t, i) =>
          i === index ? { ...t, [field]: value } : t,
        ),
      }));
    };

    const statusOptions = [
      { value: 'draft', label: 'Черновик' },
      { value: 'published', label: 'Опубликован' },
      { value: 'scheduled', label: 'Запланирован' },
      { value: 'hidden', label: 'Скрыт' },
    ];

    const ratingOptions = [
      { value: 1, label: '1 звезда' },
      { value: 2, label: '2 звезды' },
      { value: 3, label: '3 звезды' },
      { value: 4, label: '4 звезды' },
      { value: 5, label: '5 звезд' },
    ];

    const getRuTranslation = () =>
      formData.translations.find((t) => t.locale === 'ru') ||
      formData.translations[0];
    const getEnTranslation = () =>
      formData.translations.find((t) => t.locale === 'en') ||
      formData.translations[1];

    const isEditMode = !!reviewId && !!existingReview;
    const modalTitle = isEditMode ? 'Редактировать отзыв' : 'Добавить отзыв';

    return (
      <Modal isOpen={isOpen} onClose={onClose} title={modalTitle}>
        <form className={styles.form} onSubmit={handleSubmit}>
          {/* Обложка отзыва */}
          <div className={styles.formGroup}>
            <MediaSelector
              label="Обложка отзыва"
              value={formData.hero_media_id || null}
              onChange={handleMediaChange}
              placeholder="Выберите изображение для обложки"
              required
              preloadedMedia={selectedHeroMedia}
            />
          </div>

          {/* Видео отзыва */}
          <div className={styles.formGroup}>
            <VideoSelector
              label="Видео отзыва"
              value={{
                provider: formData.video_provider,
                video_id: formData.video_id,
                video_url: formData.video_url,
                duration_sec: formData.video_duration_sec,
              }}
              onChange={handleVideoChangeWithState}
              placeholder="Выберите видео для отзыва"
              preloadedVideo={selectedVideoMedia}
            />
          </div>

          {/* Русский перевод */}
          <div className={styles.languageSection}>
            <h3 className={styles.languageTitle}>🇷🇺 Русский</h3>

            <div className={styles.formGroup}>
              <label className={styles.label}>Заголовок отзыва (RU)*</label>
              <input
                type="text"
                className={styles.input}
                value={getRuTranslation()?.title || ''}
                onChange={(e) =>
                  handleTranslationChange(0, 'title', e.target.value)
                }
                placeholder="Введите заголовок отзыва на русском"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Текст отзыва (RU)*</label>
              <textarea
                className={styles.textarea}
                value={getRuTranslation()?.body || ''}
                onChange={(e) =>
                  handleTranslationChange(0, 'body', e.target.value)
                }
                placeholder="Введите текст отзыва на русском"
                rows={4}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Краткое описание (RU)</label>
              <textarea
                className={styles.textarea}
                value={getRuTranslation()?.summary || ''}
                onChange={(e) =>
                  handleTranslationChange(0, 'summary', e.target.value)
                }
                placeholder="Краткое описание отзыва на русском"
                rows={2}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Slug (URL) (RU)</label>
              <input
                type="text"
                className={styles.input}
                value={getRuTranslation()?.slug || ''}
                onChange={(e) =>
                  handleTranslationChange(0, 'slug', e.target.value)
                }
                placeholder="Автоматически из заголовка"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Текст транскрипции (RU)</label>
              <textarea
                className={styles.textarea}
                value={getRuTranslation()?.transcript_text || ''}
                onChange={(e) =>
                  handleTranslationChange(0, 'transcript_text', e.target.value)
                }
                placeholder="Транскрипция видео на русском"
                rows={3}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>SEO заголовок (RU)</label>
              <input
                type="text"
                className={styles.input}
                value={getRuTranslation()?.seo_title || ''}
                onChange={(e) =>
                  handleTranslationChange(0, 'seo_title', e.target.value)
                }
                placeholder="SEO заголовок для поисковых систем"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>SEO описание (RU)</label>
              <textarea
                className={styles.textarea}
                value={getRuTranslation()?.seo_description || ''}
                onChange={(e) =>
                  handleTranslationChange(0, 'seo_description', e.target.value)
                }
                placeholder="SEO описание для поисковых систем"
                rows={2}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                Alt текст для изображения (RU)
              </label>
              <input
                type="text"
                className={styles.input}
                value={getRuTranslation()?.alt_hero || ''}
                onChange={(e) =>
                  handleTranslationChange(0, 'alt_hero', e.target.value)
                }
                placeholder="Описание главного изображения"
              />
            </div>
          </div>

          {/* Английский перевод */}
          <div className={styles.languageSection}>
            <h3 className={styles.languageTitle}>🇺🇸 English</h3>

            <div className={styles.formGroup}>
              <label className={styles.label}>Review Title (EN)*</label>
              <input
                type="text"
                className={styles.input}
                value={getEnTranslation()?.title || ''}
                onChange={(e) =>
                  handleTranslationChange(1, 'title', e.target.value)
                }
                placeholder="Enter review title in English"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Review Text (EN)*</label>
              <textarea
                className={styles.textarea}
                value={getEnTranslation()?.body || ''}
                onChange={(e) =>
                  handleTranslationChange(1, 'body', e.target.value)
                }
                placeholder="Enter review text in English"
                rows={4}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Summary (EN)</label>
              <textarea
                className={styles.textarea}
                value={getEnTranslation()?.summary || ''}
                onChange={(e) =>
                  handleTranslationChange(1, 'summary', e.target.value)
                }
                placeholder="Short review summary in English"
                rows={2}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Slug (URL) (EN)</label>
              <input
                type="text"
                className={styles.input}
                value={getEnTranslation()?.slug || ''}
                onChange={(e) =>
                  handleTranslationChange(1, 'slug', e.target.value)
                }
                placeholder="Auto-generated from title"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Transcript Text (EN)</label>
              <textarea
                className={styles.textarea}
                value={getEnTranslation()?.transcript_text || ''}
                onChange={(e) =>
                  handleTranslationChange(1, 'transcript_text', e.target.value)
                }
                placeholder="Video transcript in English"
                rows={3}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>SEO Title (EN)</label>
              <input
                type="text"
                className={styles.input}
                value={getEnTranslation()?.seo_title || ''}
                onChange={(e) =>
                  handleTranslationChange(1, 'seo_title', e.target.value)
                }
                placeholder="SEO title for search engines"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>SEO Description (EN)</label>
              <textarea
                className={styles.textarea}
                value={getEnTranslation()?.seo_description || ''}
                onChange={(e) =>
                  handleTranslationChange(1, 'seo_description', e.target.value)
                }
                placeholder="SEO description for search engines"
                rows={2}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Alt text for image (EN)</label>
              <input
                type="text"
                className={styles.input}
                value={getEnTranslation()?.alt_hero || ''}
                onChange={(e) =>
                  handleTranslationChange(1, 'alt_hero', e.target.value)
                }
                placeholder="Main image description"
              />
            </div>
          </div>

          {/* Связи и категории */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Связи и категории</h3>

            <div className={styles.row}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Курс</label>
                <CourseSelector
                  value={formData.course_id}
                  onChange={(courseId) =>
                    handleInputChange('course_id', courseId || '')
                  }
                  placeholder="Выберите курс"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Автор отзыва</label>
                <PersonSelector
                  value={formData.author_id}
                  onChange={(authorId) =>
                    handleInputChange('author_id', authorId || '')
                  }
                  placeholder="Выберите автора"
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Страна</label>
                <CountrySelector
                  value={formData.country_id}
                  onChange={(countryId) =>
                    handleInputChange('country_id', countryId || '')
                  }
                  placeholder="Выберите страну"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Город</label>
                <CitySelector
                  value={formData.city_id}
                  onChange={(cityId) =>
                    handleInputChange('city_id', cityId || '')
                  }
                  placeholder="Выберите город"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Теги</label>
              <TagSelector
                value={formData.tag_ids || []}
                onChange={(tagIds) => handleInputChange('tag_ids', tagIds)}
                placeholder="Выберите теги"
              />
            </div>
          </div>

          {/* Настройки отзыва */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Настройки отзыва</h3>

            <div className={styles.row}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Статус</label>
                <select
                  className={styles.select}
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Рейтинг</label>
                <select
                  className={styles.select}
                  value={formData.rating}
                  onChange={(e) =>
                    handleInputChange('rating', parseInt(e.target.value))
                  }
                >
                  {ratingOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={formData.is_featured}
                  onChange={(e) =>
                    handleInputChange('is_featured', e.target.checked)
                  }
                />
                Рекомендуемый отзыв
              </label>
            </div>
          </div>

          {/* Кнопки */}
          <div className={styles.buttonGroup}>
            <Button
              type="button"
              appearance="back"
              onClick={onClose}
              disabled={loading}
            >
              Отменить
            </Button>
            <Button type="submit" appearance="auth" disabled={loading}>
              {loading
                ? isEditMode
                  ? 'Сохранение...'
                  : 'Создание...'
                : isEditMode
                ? 'Сохранить изменения'
                : 'Создать отзыв'}
            </Button>
          </div>
        </form>
      </Modal>
    );
  },
);
